import { addDays, formatISO, parseISO } from 'date-fns';
import type { Api } from '../api';

export type Status =
  | 'pending'
  | 'active'
  | 'active_ending'
  | 'inactive'
  | 'payment_overdue'
  | 'payment_failed'
  | 'licence_revoked';

export type Source = 'stripe' | 'in_app' | 'manual' | 'b2b';

export type PlanDuration = 'twelve_month' | 'four_month' | 'custom';

const PLAN_DURATION_LABEL: Partial<Record<PlanDuration, string>> = {
  twelve_month: '12 month',
  four_month: '4 month',
};

/** Badge label for the subscription term, or undefined when there's none to show (custom, B2B). */
export function getPlanDurationLabel(
  value: string | null | undefined,
): string | undefined {
  return PLAN_DURATION_LABEL[value as PlanDuration];
}

export type DisplayProps = {
  status: Status;
  source?: Source;
  endsAt?: string | null;
  retryUntil?: string | null;
  isIndividual?: boolean;
};

type ApiSub =
  | Api.UserStats.V['subscription']
  | Api.User.V_ListAsAdmin['subscriptionInfo']
  | Api.User.V_ClientList['subscriptionInfo'];

const STRIPE_DUNNING_DAYS = 14;

export function toDisplayProps(sub: ApiSub | undefined): DisplayProps {
  if (!sub) return { status: 'inactive' };

  const isB2B = sub.licence?.team != null;
  const status = parseStatus(sub.status);
  const source = parseSource(sub.source) ?? (isB2B ? 'b2b' : undefined);

  const endsAt = isB2B
    ? sub.licence?.expiresAt
    : source === 'manual'
      ? sub.subscription?.inactiveAt
      : sub.subscription?.autoRenewCanceledAt
        ? sub.subscription.inactiveAt
        : null;

  const retryUntil =
    status === 'payment_overdue' && sub.subscription?.paymentOverdueAt
      ? formatISO(
          addDays(
            parseISO(sub.subscription.paymentOverdueAt),
            STRIPE_DUNNING_DAYS,
          ),
        )
      : null;

  return {
    status,
    source,
    endsAt,
    retryUntil,
    isIndividual: !isB2B,
  };
}

function parseStatus(value: string | null): Status {
  switch (value) {
    case 'pending':
    case 'active':
    case 'active_ending':
    case 'inactive':
    case 'payment_overdue':
    case 'payment_failed':
    case 'licence_revoked':
      return value;
    case null:
      return 'inactive';
    default:
      throw new Error(`Unknown Subscription status: ${value}`);
  }
}

function parseSource(value: string | null): Source | undefined {
  switch (value) {
    case 'stripe':
    case 'in_app':
    case 'manual':
    case 'b2b':
      return value;
    case null:
      return undefined;
    default:
      throw new Error(`Unknown Subscription source: ${value}`);
  }
}
